/* eslint-disable */
import Vue from "vue";
import createAuth0Client from "@auth0/auth0-spa-js";
import jwt_decode from "jwt-decode";
import isAfter from "date-fns/is_after";
import subtractMinutes from "date-fns/sub_minutes";
import addSeconds from "date-fns/add_seconds";
import differenceInMilliSeconds from "date-fns/difference_in_milliseconds";

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;
let refreshTimeout = null;
export const getInstance = () => instance;
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  store = this.$store,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
        permissions: [],
        rsosToken: null,
      };
    },
    props: {
      store: Object,
    },
    methods: {
      async loginWithPopup(options, config) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(options, config);
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          this.error = null;
        } catch (e) {
          console.error(e);
          this.error = e;
        } finally {
          this.popupOpen = false;
        }
      },
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          const jwtToken = await this.getTokenSilently();
          store.commit("user/setUserJwt", jwtToken);
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      initSession(tokenDecoded) {
        //token silently refresh
        let tokenExpiryDate = tokenDecoded.exp;
        if (!tokenExpiryDate) {
          //No token expiry date. user probably never logged ins
          return this.logout();
        }

        const minutesBeforeExpity = 10;
        let tenMinutesBeforeExpiry = subtractMinutes(
          new Date(tokenExpiryDate * 1000),
          minutesBeforeExpity
        ); //If the token has expired or will expire in the next 30 minutes
        const now = new Date();

        if (isAfter(now, tenMinutesBeforeExpiry)) {
          //If the token has expired or will expire in the next 10 minutes
          return this.logout();
        }

        const diff = differenceInMilliSeconds(tenMinutesBeforeExpiry, now);
        refreshTimeout = setTimeout(this.refreshTokens, diff);
      },
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      logout(o) {
        clearTimeout(refreshTimeout);
        refreshTimeout = null;
        this.rsosToken = null;
        return this.auth0Client.logout(o);
      },
      can(claim) {
        return this.permissions.includes(claim);
      },
      async refreshTokens() {
        const jwtToken = await this.getTokenSilently({ ignoreCache: true });
        store.commit("user/setUserJwt", jwtToken);
        try {
          const tokenDecoded = jwt_decode(jwtToken);
          const expiryTime = tokenDecoded.exp - tokenDecoded.iat;
          const tokenExpiryDate = addSeconds(new Date(), expiryTime);
          const minutesBeforeExpity = 10;
          const tenMinutesBeforeExpiry = subtractMinutes(
            tokenExpiryDate,
            minutesBeforeExpity
          );
          const now = new Date();
          const diff = differenceInMilliSeconds(tenMinutesBeforeExpiry, now);
          refreshTimeout = setTimeout(this.refreshTokens, diff, this);
        } catch (err) {
          /* eslint-disable */
          console.error("Failed to parse JWT permissions - " + err);
        }
      },
      saveRsosToken(token) {
        this.rsosToken = token;
        this.$cookie.set("x-rsos-token", token, 1);
      },
      saveAgencyId(agencyId) {
        this.$cookie.set("x-current-agency", agencyId, 1);
      }
    },
    async created() {
      this.rsosToken = null;
      this.auth0Client = await createAuth0Client({
        ...options,
        client_id: options.clientId,
        redirect_uri: redirectUri,
      });

      try {
        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          const { appState } = await this.auth0Client.handleRedirectCallback();
          this.error = null;
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        if (this.isAuthenticated === true) {
          this.user = await this.auth0Client.getUser();
          const token = await this.getTokenSilently();
          store.commit("user/setUserJwt", token);

          try {
            const tokenDecoded = jwt_decode(token);
            this.permissions = tokenDecoded.permissions;
            //token silently refresh
            this.initSession(tokenDecoded);
          } catch (err) {
            /* eslint-disable */
            console.error("Failed to parse JWT permissions - " + err);
          }
          store.dispatch("user/fetchPendingInvitations", {
            token,
            userEmail: this.user.email,
          });
        }
        this.loading = false;
      }
    },
  });

  return instance;
};

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  },
};
